import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Package,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Download,
  Filter,
  ShoppingCart,
  Search,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIEEN = ['Borstels', 'Chemie', 'Techniek', 'Shop producten', 'Overig'];

const VoorraadOverzicht = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [locaties, setLocaties] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    categorie: searchParams.get('categorie') || '',
    locatie: searchParams.get('locatie') || ''
  });

  const [activeTab, setActiveTab] = useState('bestel');

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.categorie) params.append('categorie', filters.categorie);
      if (filters.locatie) params.append('locatie_id', filters.locatie);
      
      const response = await axios.get(`${API}/management/voorraad?${params}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching voorraad:', error);
      toast.error('Fout bij laden voorraad');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchLocaties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/locaties`);
      setLocaties(response.data);
    } catch (error) {
      console.error('Error fetching locaties:', error);
    }
  }, []);

  useEffect(() => {
    fetchLocaties();
  }, [fetchLocaties]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    if (newFilters.status) params.set('status', newFilters.status);
    if (newFilters.categorie) params.set('categorie', newFilters.categorie);
    if (newFilters.locatie) params.set('locatie', newFilters.locatie);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({ status: '', categorie: '', locatie: '' });
    setSearchParams({});
  };

  const exportCSV = () => {
    if (!data?.items) return;
    
    const headers = ['Locatie', 'Product', 'Categorie', 'Eenheid', 'Standaard', 'Huidig', 'Status'];
    const rows = data.items.map(item => [
      item.locatieNaam,
      item.productNaam,
      item.categorie,
      item.eenheid,
      item.standaard,
      item.huidig,
      item.status
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `voorraad-overzicht-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV geëxporteerd');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'kritiek':
        return <Badge variant="destructive" className="gap-1"><AlertCircle className="w-3 h-3" />Kritiek</Badge>;
      case 'waarschuwing':
        return <Badge variant="secondary" className="bg-amber-100 text-amber-800 gap-1"><AlertTriangle className="w-3 h-3" />Laag</Badge>;
      default:
        return <Badge variant="secondary" className="bg-green-100 text-green-800 gap-1"><CheckCircle className="w-3 h-3" />OK</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-500">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1f2548] flex items-center gap-2">
            <Package className="w-7 h-7" />
            Voorraad Overzicht
          </h1>
          <p className="text-slate-500">Voorraadstatus alle locaties</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
        <button onClick={() => setActiveTab('bestel')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${activeTab === 'bestel' ? 'bg-white text-[#1f2548] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          <ShoppingCart className="w-4 h-4 inline mr-1" /> Bestelvoorraad
        </button>
        <button onClick={() => setActiveTab('locatie')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${activeTab === 'locatie' ? 'bg-white text-[#1f2548] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          <Package className="w-4 h-4 inline mr-1" /> Locatievoorraad
        </button>
      </div>

      {activeTab === 'bestel' && <BestelVoorraadTab locaties={locaties} />}

      {activeTab === 'locatie' && (
        <>
      <div className="flex justify-end">
        <Button onClick={exportCSV} variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Exporteer CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-slate-800">{data?.stats?.totaal || 0}</div>
            <div className="text-sm text-slate-500">Totaal items</div>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{data?.stats?.kritiek || 0}</div>
            <div className="text-sm text-slate-500">Kritiek</div>
          </CardContent>
        </Card>
        <Card className="border-amber-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">{data?.stats?.waarschuwing || 0}</div>
            <div className="text-sm text-slate-500">Waarschuwing</div>
          </CardContent>
        </Card>
        <Card className="border-green-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{data?.stats?.ok || 0}</div>
            <div className="text-sm text-slate-500">OK</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filters:</span>
            </div>
            
            <Select value={filters.status} onValueChange={(v) => handleFilterChange('status', v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="kritiek">Kritiek</SelectItem>
                <SelectItem value="waarschuwing">Waarschuwing</SelectItem>
                <SelectItem value="ok">OK</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.categorie} onValueChange={(v) => handleFilterChange('categorie', v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Categorie" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIEEN.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filters.locatie} onValueChange={(v) => handleFilterChange('locatie', v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Locatie" />
              </SelectTrigger>
              <SelectContent>
                {locaties.map(loc => (
                  <SelectItem key={loc._id} value={loc._id}>{loc.naam}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {(filters.status || filters.categorie || filters.locatie) && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Wis filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Voorraad items ({data?.items?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-3 font-medium text-slate-500">Locatie</th>
                  <th className="pb-3 font-medium text-slate-500">Product</th>
                  <th className="pb-3 font-medium text-slate-500">Categorie</th>
                  <th className="pb-3 font-medium text-slate-500">Standaard</th>
                  <th className="pb-3 font-medium text-slate-500">Huidig</th>
                  <th className="pb-3 font-medium text-slate-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {data?.items?.map((item, idx) => (
                  <tr key={`${item.locatieId}-${item.productId}-${idx}`} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="py-3">
                      <div className="font-medium text-slate-800">{item.locatieNaam}</div>
                    </td>
                    <td className="py-3 font-medium">{item.productNaam}</td>
                    <td className="py-3 text-slate-600">{item.categorie}</td>
                    <td className="py-3 text-slate-600">{item.standaard} {item.eenheid}</td>
                    <td className="py-3">
                      <span className={`font-medium ${item.status === 'kritiek' ? 'text-red-600' : item.status === 'waarschuwing' ? 'text-amber-600' : 'text-green-600'}`}>
                        {item.huidig}
                      </span>
                      <span className="text-slate-500 ml-1">{item.eenheid}</span>
                    </td>
                    <td className="py-3">{getStatusBadge(item.status)}</td>
                  </tr>
                ))}
                {(!data?.items || data.items.length === 0) && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-slate-500">
                      Geen voorraad items gevonden
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
};

// ── BestelVoorraad Tab ──────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const BestelVoorraadTab = ({ locaties }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoc, setFilterLoc] = useState('alle');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const params = new URLSearchParams();
    if (filterLoc !== 'alle') params.set('locatie_id', filterLoc);
    fetch(`${API}/management/voorraad-bestellingen?${params}`, {
      headers: { Authorization: `Bearer ${getAccessToken()}` }
    })
      .then(r => r.ok ? r.json() : [])
      .then(setData)
      .finally(() => setLoading(false));
  }, [filterLoc]);

  const filtered = data.filter(d => !search || d.product_naam?.toLowerCase().includes(search.toLowerCase()));

  // Group by locatie
  const grouped = {};
  filtered.forEach(d => {
    const key = d.locatie_naam || 'Onbekend';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(d);
  });

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#1f2548]" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex gap-3 flex-wrap bg-white rounded-xl border p-3">
        <Select value={filterLoc} onValueChange={setFilterLoc}>
          <SelectTrigger className="w-52"><SelectValue placeholder="Locatie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle locaties</SelectItem>
            {locaties.map(l => <SelectItem key={l._id} value={l._id}>{l.naam}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Zoek product..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
        </div>
        <Badge className="bg-[#1f2548] text-white self-center">{filtered.length} producten</Badge>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <Card><CardContent className="py-8 text-center text-slate-400">Nog geen producten besteld via de Hub</CardContent></Card>
      ) : (
        Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0])).map(([locNaam, items]) => (
          <Card key={locNaam}>
            <CardHeader className="bg-slate-50 border-b py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-[#1f2548]">{locNaam}</CardTitle>
                <Badge variant="outline">{items.length} producten</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-slate-500">
                      <th className="py-2 px-4">Product</th>
                      <th className="py-2 px-4">Leverancier</th>
                      <th className="py-2 px-4 text-center">Voorraad</th>
                      <th className="py-2 px-4 text-center">Laatste besteld</th>
                      <th className="py-2 px-4">Datum</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {items.map((it, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="py-2 px-4 font-medium text-slate-700">{it.product_naam}</td>
                        <td className="py-2 px-4 text-slate-500 text-xs">{it.leverancier_naam || '-'}</td>
                        <td className="py-2 px-4 text-center font-bold text-[#1f2548]">{it.huidige_voorraad}</td>
                        <td className="py-2 px-4 text-center text-slate-600">{it.laatste_besteld_aantal || '-'}</td>
                        <td className="py-2 px-4 text-slate-400 text-xs">{fmtDate(it.laatste_besteld_datum)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default VoorraadOverzicht;
